/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, computed, DoCheck, HostListener, OnInit, signal} from '@angular/core';
import {
    CellEditingStoppedEvent,
    ColDef,
    ColGroupDef,
    DragStoppedEvent,
    GridApi,
    GridReadyEvent,
} from 'ag-grid-community';
import {KtsTimingTemplateRow} from '../../model/ktsTimingTemplateRow.model';
import {KtsComponent} from '../kts/kts.component';
import {MenuItem} from 'primeng/api';
import {CommonHelperService} from '../../service/common-helper.service';
import {ActionsCellRendererComponent} from '../cell-renderer/actions-cell-renderer.component';
import {TypeaheadCellEditorComponent} from '../cell-renderer/typeahead-cell-editor.component';
import {ValidationModel} from '../../model/validation.model';
import {refreshCellAndDeferredRowRedraw} from '../cell-renderer/prime-cell-editor-tab.util';

@Component({
    selector: 'app-kts-editor',
    templateUrl: './kts-editor.component.html',
    styleUrls: ['./kts-editor.component.scss'],
    standalone: false
})
export class KtsEditorComponent extends KtsComponent implements OnInit, DoCheck {

    // Signal consumed by the template so options updates are explicit and stable.
    currentTemplateVersions = signal<string[]>([]);

    private readonly _dataChanged = signal(false);
    private readonly _duplicate = signal(false);
    private readonly _rowsInvalid = signal(false);
    private readonly _currentVersionStatus = signal('');
    readonly saveAllDisabled = computed(() => !this._dataChanged() || this._duplicate() || this._rowsInvalid());
    readonly saveAllSeverity = computed<'secondary' | null>(() => this.saveAllDisabled() ? 'secondary' : null);
    readonly isCurrentVersionDraft = computed(() => this._currentVersionStatus() === 'DRAFT');
    readonly isCurrentVersionActive = computed(() => this._currentVersionStatus() === 'ACTIVE');

    // Key is type (PROD/AVD/ECS) which then goes to a map with version as key to template rows map
    ktsTimingTemplateRowMap = new Map<string, Map<string, Array<KtsTimingTemplateRow>>>();
    showNewVersionModal: boolean;
    showNewRevisionModal: boolean;
    selectedVersionToCopy: string;
    templateVersions = new Map<string, string[]>;
    templateFullVersions = new Map<string, string[]>;
    newTemplateVersion: number;
    // default prefixes, pulling from database and overwriting
    versionPrefixes = ['Fast', 'PPTB', 'FastPPTB'];
    newVersionPrefix: string;
    activeTabIndex = 0;
    permissionUrl = '/esciUI/ktsEditorPermission';
    saveUrl = '/esciUI/saveKtsTimingTemplateRows';
    templateUrl = '/esciUI/ktsTimingTemplatesForEditor';
    public downloadTemplateButtonOptions = new Map<string, MenuItem[]>;
    public deleteDraftVersionOptions = new Map<string, MenuItem[]>;
    protected readonly window = window;
    roleOptions = new Map<string, string[]>;
    phaseOptions = new Map<string, string[]>;
    milestoneOptions = new Map<string, string[]>;
    includeOptions = new Map<string, string[]>();
    chartLabelOptions = new Map<string, string[]>();
    buildEventOptions = new Map<string, string[]>();
    fullCols: Map<string, Map<string, (ColDef | ColGroupDef)[]>> = new Map;
    cols: (ColDef | ColGroupDef)[] = [
        {field: 'role', headerName: 'Role'},
        {field: 'phase', headerName: 'Phase', maxWidth: 200, minWidth: 100},
        {
            headerName: 'Deliverable & Description',
            marryChildren: true,
            children: [
                {field: 'deliverable', headerName: 'Deliverable', minWidth: 500, maxWidth: 1000,},
                {
                    field: 'description',
                    headerName: 'Description',
                    columnGroupShow: 'open',
                    minWidth: 500,
                    maxWidth: 1200
                },
            ],
        },
        {field: 'milestone', headerName: 'Milestone', minWidth: 100, maxWidth: 300},
        {
            field: 'weeksFromMilestone', headerName: 'Weeks From Milestone', minWidth: 100, maxWidth: 200,
            cellStyle: {'text-align': 'center'}
        },
        {field: 'includeInEsorReport', headerName: 'eSOR Submission', minWidth: 120, maxWidth: 150},
        {
            field: 'duration', headerName: 'Duration', minWidth: 100, maxWidth: 300,
            cellStyle: {'text-align': 'center'}
        },
        {field: 'chartLabel', headerName: 'Gantt Chart Label', minWidth: 100, maxWidth: 300},
        {field: 'batchBuildEvent', headerName: 'Batch Build Event', minWidth: 300, maxWidth: 300},
    ];
    defaultColDef: ColDef = {
        floatingFilter: true,
        resizable: true,
        sortable: false,
        filter: true,
        minWidth: 70,
        maxWidth: 100,
        editable: true,
        cellClassRules: this.cellClassRules
    };
    version: string;
    get allowsVersionPrefixes(): boolean {
        return this.type !== 'ECS';
    }
    get allowsNewMajorVersion(): boolean {
        return this.type !== 'ECS';
    }
    gridApiMap: Map<string, GridApi> = new Map<string, GridApi>();
    saving: boolean;
    exporting: boolean;
    resetting: boolean;
    dataChanged: boolean;
    duplicate: boolean;
    rowsInvalid: boolean;
    get hasTabs(): boolean {
        return !!this.fullCols.get(this.type)?.size;
    }
    get safeActiveTabIndex(): number {
        if (!this.hasTabs) {
            return 0;
        }
        const maxIndex = Math.max(0, this.fullCols.get(this.type).size - 1);
        const currentIndex = typeof this.activeTabIndex === 'number' ? this.activeTabIndex : 0;
        return Math.min(Math.max(0, currentIndex), maxIndex);
    }

    ngOnInit(): void {
        this.permission.set(false);
        this.esciService.getPermissionAndFileSizeLimit(this, false);
        this.esciService.getKtsTimingTemplatePrefixes(this);
        this.exporting = true;
        this.esciService.getKtsTimingTemplates(this, true, () => this.setButtonOptionsAndDropdowns());
        this.templateVersions.set(this.type, []);
        this.syncCurrentTemplateVersions();
    }

    ngDoCheck(): void {
        // Mirror mutable base-class booleans into signals used by template bindings.
        this._dataChanged.set(!!this.dataChanged);
        this._duplicate.set(!!this.duplicate);
        this._rowsInvalid.set(!!this.rowsInvalid);
        this._currentVersionStatus.set(this.version ? this.getStatusOfVersion(this.version, this.type) : '');
    }

    private syncCurrentTemplateVersions(): void {
        let versions = [...(this.templateVersions.get(this.type) || [])];
        if (this.type === 'ECS') {
            versions = versions.filter(version =>
                KtsTimingTemplateRow.getMajorVersionFromFullVersion(version) === '5.8');
        }
        this.currentTemplateVersions.set(versions);
    }

    onTypeChange() {
        const colsForType = this.fullCols.get(this.type);
        if (!colsForType || colsForType.size === 0) {
            this.activeTabIndex = -1;
            this.version = null;
            this.syncCurrentTemplateVersions();
            return;
        }
        this.activeTabIndex = 0;
        this.version = colsForType.keys().next().value;
        this.syncCurrentTemplateVersions();
    }

    onGridReady(params: GridReadyEvent, key: string) {
        this.gridApiMap.set(key, params.api);
    }

    setButtonOptionsAndDropdowns() {
        this.setDownloadButtonOptions();
        this.setDropdownOptions();
        for (const templateType of this.ktsTimingTemplateRowMap.keys()) {
            this.templateFullVersions.set(templateType, [...this.ktsTimingTemplateRowMap.get(templateType).keys()]);
            this.templateVersions.set(templateType, [...this.templateFullVersions.get(templateType)
                .filter(version => this.isVersionActive(version, templateType))
                .map(version => KtsTimingTemplateRow.getMajorVersionFromFullVersion(version))]);
        }
        this.deleteDraftVersionOptions = new Map();
        this.types.forEach(templateType => {
            const draftOptions = [];
            this.ktsTimingTemplateRowMap.get(templateType)?.forEach((value, version) => {
                if (this.isVersionDraft(version, templateType)) {
                    draftOptions.push({
                        label: `${version} ${this.getStatusOfVersionForLabel(version, templateType)}`,
                        icon: 'pi pi-trash',
                        command: () => this.deleteDraftTemplate(version),
                    });
                }
            });
            draftOptions.sort((a, b) => a.label.localeCompare(b.label));
            this.deleteDraftVersionOptions.set(templateType, draftOptions);
        });

        for (const templateType of this.ktsTimingTemplateRowMap.keys()) {
            this.fullCols.get(templateType).forEach((value, version) => {
                if (!this.isVersionDraft(version, templateType)) {
                    // For non-draft versions, set the editable attribute to false on each column
                    this.getColsFromColDefOrColGroupDef(value).forEach(col => col.editable = false);
                } else {
                    this.getColsFromColDefOrColGroupDef(value).forEach(col => {
                        col.tooltipValueGetter = (params) => {
                            return params.data.changes?.get(col.field);
                        };
                    });
                    // For drafts, add the actions column
                    const actionColumn = {
                        minWidth: 130,
                        maxWidth: 130,
                        headerName: 'Actions',
                        rowDrag: true,
                        cellRenderer: ActionsCellRendererComponent,
                        cellRendererParams: {
                            onDeleteClick: this.onDeleteClick.bind(this),
                            onCopyClick: this.onCopyClick.bind(this),
                            fieldForDeleteMessage: 'deliverable'
                        },
                        editable: false,
                        filter: false,
                        sortable: false,
                    lockPosition: true,
                    suppressMovable: true,
                    };
                    // add new col to beginning of value
                    value.unshift(actionColumn);
                }
            });
        }
        this.onTypeChange();
        this.syncCurrentTemplateVersions();
        // One refresh after column defs and row data are final (avoid an extra pass mid-setup).
        this.refreshTemplateEditorGrid();
    }

    setDownloadButtonOptions() {
        this.resetting = false;
        this.exporting = false;
        if (!this.version) {
            this.version = this.fullCols.get(this.type).keys().next().value;
        }
        this.downloadTemplateButtonOptions = new Map();

        this.types.forEach(type => {
            const buttons = [];
            this.ktsTimingTemplateRowMap.get(type)?.forEach((value, version) => {
                buttons.push({
                    label: `${version} ${this.getStatusOfVersionForLabel(version, type)}`,
                    icon: 'pi pi-download',
                    command: () => this.exportTemplateReport(version, type),
                });
            });
            buttons.sort((a, b) => a.label.localeCompare(b.label));
            this.downloadTemplateButtonOptions.set(type, buttons);
        });
    }

    setDropdownOptions() {
        // Fresh maps on each reload so removed template versions do not leave stale dropdown keys.
        this.roleOptions = new Map<string, string[]>();
        this.phaseOptions = new Map<string, string[]>();
        this.milestoneOptions = new Map<string, string[]>();
        this.includeOptions = new Map<string, string[]>();
        this.chartLabelOptions = new Map<string, string[]>();
        this.buildEventOptions = new Map<string, string[]>();
        this.setDropdownOptionMap(this.roleOptions, 'role');
        this.setDropdownOptionMap(this.phaseOptions, 'phase');
        // for our milestone dropdown, we want all milestones along with all non-milestone deliverables
        this.setDropdownOptionMap(this.milestoneOptions, 'milestone', 'deliverable');
        this.setDropdownOptionMap(this.includeOptions, 'includeInEsorReport');
        this.setDropdownOptionMap(this.chartLabelOptions, 'chartLabel');
        this.setDropdownOptionMap(this.buildEventOptions, 'batchBuildEvent');
        const ktsRow = new KtsTimingTemplateRow(null);
        const rulesByField = new Map(ktsRow.fieldRules.map((rule) => [rule.fieldName, rule]));
        const fullColsForType = this.fullCols.get(this.type);
        if (!fullColsForType) {
            return;
        }
        for (const [, colDefs] of fullColsForType.entries()) {
            for (const col of this.getColsFromColDefOrColGroupDef(colDefs)) {
                const rule = rulesByField.get(col.field);
                // If not using dropdowns, use the field rules
                if (!rule || col.cellEditor === TypeaheadCellEditorComponent) {
                    continue;
                }
                if (rule.type === 'number') {
                    col.cellEditor = 'agNumberCellEditor';
                    col.cellEditorParams = {};
                    col.cellEditorParams.max = rule.maxLength;
                    if (rule.minLength) {
                        col.cellEditorParams.min = rule.minLength;
                    }
                } else {
                    col.cellEditor = 'agTextCellEditor';
                    col.cellEditorParams = {maxLength: rule.maxLength};
                }
                col.width = rule.maxLength;
            }
        }
    }

    setDropdownOptionMap(optionMap: Map<string, string[]>, field: string, field2?: string) {
        const typeMap = this.ktsTimingTemplateRowMap.get(this.type);
        if (!typeMap) {
            return;
        }
        typeMap.forEach((rows, versionKey) => {
            const seen = new Set<string>();
            const values: string[] = [''];
            seen.add('');
            const pushUnique = (s: string | undefined | null) => {
                if (s == null || s === '') {
                    return;
                }
                if (!seen.has(s)) {
                    seen.add(s);
                    values.push(s);
                }
            };
            for (const row of rows) {
                pushUnique(row[field] as string);
            }
            if (field2) {
                for (const row of rows) {
                    if (field2 === 'deliverable' && row.phase === 'Milestone') {
                        // do not add deliverables from real milestone rows
                        // as these rows have true milestones like "PFI", "DFI", etc
                        continue;
                    }
                    pushUnique(row[field2] as string);
                }
            }
            optionMap.set(versionKey, values);
        });
        const fullColsForType = this.fullCols.get(this.type);
        if (!fullColsForType) {
            return;
        }
        for (const [versionKey, colDefs] of fullColsForType.entries()) {
            for (const col of this.getColsFromColDefOrColGroupDef(colDefs)) {
                if (col.field === field) {
                    col.cellEditor = TypeaheadCellEditorComponent;
                    col.cellEditorParams = {values: optionMap.get(versionKey)};
                }
            }
        }
    }

    /** Template editor grid refresh (do not name `refreshGrid` — base class uses that signature for save flows). */
    refreshTemplateEditorGrid(version?: string, rowIndex?: number) {
        // Defer one macrotask so ag-Grid sees the latest columnDefs; keep delay minimal for Reset / reload UX.
        setTimeout(() => {
            const api = this.gridApiMap.get(this.version);
            if (api && !api.isDestroyed()) {
                api.setGridOption('rowData', this.ktsTimingTemplateRowMap.get(this.type).get(this.version));
                api.sizeColumnsToFit();
            }
            // After rowData replace, scroll/focus the target row (index 0 is valid — do not use `if (index)`).
            if (version != null && typeof rowIndex === 'number' && rowIndex >= 0) {
                const versionApi = this.gridApiMap.get(version);
                if (versionApi && !versionApi.isDestroyed()) {
                    const rowCount = versionApi.getDisplayedRowCount();
                    const safeRow = Math.min(rowIndex, Math.max(0, rowCount - 1));
                    const firstCol = versionApi.getAllDisplayedColumns()[0];
                    if (firstCol) {
                        versionApi.ensureIndexVisible(safeRow);
                        versionApi.ensureColumnVisible(firstCol);
                        versionApi.setFocusedCell(safeRow, firstCol);
                    }
                }
            }
        }, 0);
    }

    saveTimingTemplates(targetStatus?: string) {
        if (this.areRowsValid(this.ktsTimingTemplateRowMap.get(this.type))) {
            // if targetStatus is provided, we need to update the status of the rows for the current version
            if (targetStatus) {
                this.confirmationService.confirm({
                    message: `Are you sure you want to publish this template version?`,
                    header: 'Template Publish Confirmation',
                    key: 'templatePublishConfirmation',
                    icon: 'pi pi-exclamation-circle',
                    acceptButtonStyleClass: 'p-button-text',
                    rejectButtonStyleClass: 'p-button-text',
                    acceptIcon: 'pi pi-check mr-2',
                    rejectIcon: 'pi pi-times mr-2',

                    accept: () => {
                        // set the version that is one before this to ARCHIVED status
                        const majorVersion = KtsTimingTemplateRow.getMajorVersionFromFullVersion(this.version);
                        const minorVersion = KtsTimingTemplateRow.getMinorVersionFromFullVersion(this.version);
                        for (const [key, value] of this.ktsTimingTemplateRowMap.get(this.type).entries()) {
                            if (key === majorVersion + '.' + (parseInt(minorVersion, 0) - 1)) {
                                if (this.ktsTimingTemplateRowMap.get(this.type).get(key)[0].status === 'ACTIVE') {
                                    this.ktsTimingTemplateRowMap.get(this.type).get(key).forEach(row => {
                                        row.status = 'ARCHIVED';
                                        row.state = 'changed'
                                    });
                                }
                                // remove it from the grid map
                                this.fullCols.get(this.type).delete(key);
                                this.gridApiMap.delete(key);
                            }
                        }
                        // set the new version we are publishing to active
                        this.ktsTimingTemplateRowMap.get(this.type).get(this.version).forEach(row => {
                            row.status = targetStatus;
                            row.state = 'changed';
                        });
                        this.callSave();
                    }
                });
            } else {
                this.callSave();
            }
            setTimeout(() => {
                this.handleTabClick(this.version);
                this.dataChanged = false;
            }, 2000);
        }
    }

    resetChanges() {
        this.resetting = true;
        this.esciService.getKtsTimingTemplates(this, true, () => this.setButtonOptionsAndDropdowns());
        // Set version to first key from template map
        if (!this.version) {
            this.version = this.fullCols.get(this.type).keys().next().value;
        }
        this.showNewVersionModal = false;
        this.showNewRevisionModal = false;
        this.selectedVersionToCopy = null;
        this.templateVersions.set(this.type, []);
        this.syncCurrentTemplateVersions();
        this.newTemplateVersion = null;
    }

    private exportTemplateReport(version: string, type: string) {
        this.exporting = true;
        this.downloadService.exportTemplateReport(version, type).subscribe(blob => {
            const timestamp = CommonHelperService.getTimeStampForReport();
            const fileName = `${(this.getPrefix())} Timing Template Version: ${version} ${timestamp}.xlsx`;
            CommonHelperService.downloadFile(blob, fileName);
            this.messageService.processSuccess('Report successfully generated. Please see the downloaded file.');
            this.exporting = false;
        });
    }

    // This method sets the order and rownumber of the rows after being dragged
    // If the rownumber is displayed, it will not show as updated, but behind the scenes it is updated here
    onRowDragStopped(event: DragStoppedEvent<any>, key: string) {
        this.dataChanged = true;
        const rowsToUpdate = [];
        this.gridApiMap.get(key).forEachNodeAfterFilterAndSort(function (rowNode) {
            rowsToUpdate.push(rowNode.data);
        });
        // rowsToUpdate has the latest order of rows
        // loop through and set the rowNumber property
        for (let i = 0; i < rowsToUpdate.length; i++) {
            rowsToUpdate[i].rowNumber = i + 1;
        }
        // Sort the rows
        this.ktsTimingTemplateRowMap.get(this.type).get(key).sort((a, b) => a.rowNumber - b.rowNumber);
        this.markPersistedKtsRowsChangedIfRowOrderShifted(this.ktsTimingTemplateRowMap.get(this.type).get(key));
    }

    /**
     * Save only sends rows with state new/changed/deleted. After copy, delete, or drag we renumber rows in memory;
     * without marking shifted rows as changed, the server keeps stale ROW_NUMBER and reload order is wrong.
     */
    private markPersistedKtsRowsChangedIfRowOrderShifted(rows: KtsTimingTemplateRow[] | undefined): void {
        if (!rows?.length) {
            return;
        }
        for (const row of rows) {
            if (row.state === 'deleted' || row.state === 'new') {
                continue;
            }
            const orig = row.originalRowData as KtsTimingTemplateRow | undefined;
            const origRn = orig?.rowNumber;
            if (origRn != null && row.rowNumber !== origRn) {
                row.state = 'changed';
            }
        }
    }

    /**
     * Canonical order is the in-memory array per version. Re-assign rowNumber from indices before POST so
     * ag-grid applyTransaction / rowData binding cannot leave rowNumber one step out of sync with display order.
     */
    private normalizeKtsTemplateRowNumbersBeforeSave(): void {
        this.ktsTimingTemplateRowMap.forEach((versionMap) => {
            versionMap.forEach((rows) => {
                if (!rows?.length || !rows.some(r => r.state !== 'saved')) {
                    return;
                }
                for (let i = 0; i < rows.length; i++) {
                    rows[i].rowNumber = i + 1;
                }
                this.markPersistedKtsRowsChangedIfRowOrderShifted(rows);
                for (const row of rows) {
                    if (row.state === 'new') {
                        row.originalRowData = structuredClone(row) as KtsTimingTemplateRow;
                    }
                }
            });
        });
    }

    cellEditingStopped(event: CellEditingStoppedEvent<any>) {
        this.areRowsValid(this.ktsTimingTemplateRowMap.get(this.type));
        this.checkMilestones(event);
        this.updateDataChanged(this.ktsTimingTemplateRowMap.get(this.type).get(event.data.version));
        const api = event.api;
        if (event.node && api && !api.isDestroyed() && event.column) {
            refreshCellAndDeferredRowRedraw(api, event.node, event.column);
        }
    }

    // this method allows user to create a new draft of either a new major version or a revision
    // if it is a major version, they will have entered the new version and we need to append the .0
    // if it is a revision, we need to find the full version they picked to revise, and increment the minor version
    createNewTemplate() {
        if (this.type === 'ECS' && !this.showNewRevisionModal) {
            this.messageService.processError(
                'ECS templates only support 5.8 subversions (5.8.0, 5.8.1, ...). Use Change Released Version to create the next revision.');
            return;
        }
        let newVersionString = '';
        // the user was only able to select active major versions
        // find the version in our fullVersion list that starts with this major version
        const matchingVersions = this.templateFullVersions.get(this.type).filter(o => o.startsWith(this.selectedVersionToCopy));
        if (!matchingVersions || matchingVersions.length === 0) {
            this.messageService.processError('Could not find version. Please contact IT for support.');
            return;
        }
        // update our fullVersionToCopy to be the active full version so that we can find it in our map
        const fullVersionToCopy = this.templateFullVersions.get(this.type).filter(o => o.startsWith(this.selectedVersionToCopy))
            .filter(o => this.getStatusOfVersion(o, this.type) === 'ACTIVE')[0];
        // If we are making a new revision, find the full version and increment it
        if (this.showNewRevisionModal) {
            // make sure there are no existing drafts of this version
            for (const version of matchingVersions) {
                if (this.isVersionDraft(version)) {
                    this.messageService.processError('There is already a draft in progress for this version.');
                    return;
                }
            }
            let minorVersion = KtsTimingTemplateRow.getMinorVersionFromFullVersion(fullVersionToCopy);
            minorVersion = parseInt(minorVersion, 0) + 1;
            newVersionString += this.selectedVersionToCopy + '.' + minorVersion;
            if (this.type === 'ECS' && !KtsTimingTemplateRow.isPlain58Subversion(newVersionString)) {
                this.messageService.processError(
                    `ECS templates must use 5.8 subversions only (5.8.0, 5.8.1, ...). '${newVersionString}' is not allowed.`);
                return;
            }
        } else {
            // fix newTemplateVersion to have a decimal if it does not
            // the input component is type decimal but for whatever reason trims the .0
            // validate that we don't already have this version, error message if we do
            // we now support prefixes, if one was selected add it
            newVersionString = this.newVersionPrefix || '';
            newVersionString += this.newTemplateVersion.toFixed(1) + '.0';
            if (this.ktsTimingTemplateRowMap.get(this.type).has(newVersionString)) {
                this.messageService.processError(`Version ${newVersionString} already exists. Please enter a new version.`);
                return;
            }
        }
        this.showNewVersionModal = false;
        this.showNewRevisionModal = false;

        // make a new template from the selected version to copy from and add it to the map
        const newTemplateRows = structuredClone(this.ktsTimingTemplateRowMap.get(this.type).get(fullVersionToCopy));
        // set all row ids to -1 and version to new version and status to draft and state to changed
        newTemplateRows.forEach(row => {
            row.id = -1;
            row.version = newVersionString;
            row.status = 'DRAFT';
            row.state = 'new';
        });
        this.ktsTimingTemplateRowMap.get(this.type).set(newVersionString, newTemplateRows);
        // also add it to fullCols.get(this.type)
        this.fullCols.get(this.type).set(newVersionString, structuredClone(this.cols));
        this.version = newVersionString;
        this.callSave();
        this.newTemplateVersion = null;
        this.selectedVersionToCopy = null;
        setTimeout(() => {
            this.handleTabClick(this.version);
        }, 2000);
    }

    // Callbacks for delete and copy actions
    onDeleteClick(params): void {
        console.log('Delete clicked for row', params.data);
        this.copyOrDelete(params, 'delete');
    }

    onCopyClick(params): void {
        console.log('Copy clicked for row', params.data);
        this.copyOrDelete(params, 'copy');
    }

    copyOrDelete(params, action): void {
        const version = params.data.version;
        this.version = version;
        // find the index of the row clicked on
        const ktsTimingTemplateRows = this.ktsTimingTemplateRowMap.get(this.type).get(version);
        const index = ktsTimingTemplateRows.findIndex(row => row.id === params.data.id);

        let addedRow: KtsTimingTemplateRow | null = null;
        if (action === 'copy') {
            addedRow = structuredClone(params.data) as KtsTimingTemplateRow;
            addedRow.id = -1;
            addedRow.state = 'new';
            // Insert immediately below the source row (original stays above the duplicate).
            ktsTimingTemplateRows.splice(index + 1, 0, addedRow);
        } else {
            params.data.state = 'deleted';
        }
        this.dataChanged = true;
        // loop through and reset the rowNumber properties
        for (let i = 0; i < ktsTimingTemplateRows.length; i++) {
            ktsTimingTemplateRows[i].rowNumber = i + 1;
        }
        // update the grid
        this.ktsTimingTemplateRowMap.get(this.type).set(version, ktsTimingTemplateRows);
        this.markPersistedKtsRowsChangedIfRowOrderShifted(ktsTimingTemplateRows);
        if (addedRow) {
            addedRow.originalRowData = structuredClone(addedRow) as KtsTimingTemplateRow;
        }

        const api = this.gridApiMap.get(version);
        // Full setGridOption('rowData') resets scroll and can reorder the viewport oddly. Prefer transactions.
        if (action === 'copy' && addedRow && api && !api.isDestroyed()) {
            api.applyTransaction({add: [addedRow], addIndex: index + 1});
            this.scheduleKtsGridRowFocus(api, index + 1);
            return;
        }
        if (action === 'delete' && api && !api.isDestroyed()) {
            api.applyTransaction({update: ktsTimingTemplateRows});
            this.scheduleKtsGridRowFocus(api, index);
            return;
        }

        this.refreshTemplateEditorGrid(version, index);
    }

    /** Focus after ag-grid has applied the transaction (avoids stale row counts / scroll fighting template rowData). */
    private scheduleKtsGridRowFocus(api: GridApi, rowIndex: number): void {
        const run = (): void => {
            if (typeof api.isDestroyed === 'function' && api.isDestroyed()) {
                return;
            }
            const rowCount = api.getDisplayedRowCount();
            const safeRow = Math.min(rowIndex, Math.max(0, rowCount - 1));
            const firstCol = api.getAllDisplayedColumns()[0];
            if (firstCol) {
                api.ensureIndexVisible(safeRow);
                api.ensureColumnVisible(firstCol);
                api.setFocusedCell(safeRow, firstCol);
            }
        };
        queueMicrotask(() => requestAnimationFrame(() => requestAnimationFrame(run)));
    }

    private checkMilestones(event: CellEditingStoppedEvent<any>) {
        // Check if we modified a Milestone row to have a different milestone acronym
        if (event.colDef.field === 'milestone' && event.oldValue !== event.newValue && event.data.phase === 'Milestone') {
            // check if any other milestone row in the grid has the same milestone acronym
            const duplicateRows = this.ktsTimingTemplateRowMap.get(this.type).get(event.data.fullVersion)
                .filter(row => row.milestone === event.newValue && row.phase === 'Milestone');
            if (duplicateRows.length > 1) {
                this.messageService.processError(`Milestone ${event.newValue} is already in use. Please select a different milestone.`);
                this.rowsInvalid = true;
                return;
            }
            // Ask user if they would like to update all rows using the old milestone to the new milestone value
            this.confirmationService.confirm({
                message: `Would you like to update all rows using the milestone ${event.oldValue} to ${event.newValue}?`,
                header: 'Milestone Update Confirmation',
                key: 'milestoneUpdateConfirmation',
                icon: 'pi pi-exclamation-circle',
                acceptButtonStyleClass: 'p-button-text',
                rejectButtonStyleClass: 'p-button-text',
                acceptIcon: 'pi pi-check mr-2',
                rejectIcon: 'pi pi-times mr-2',
                accept: () => {
                    this.version = event.data.fullVersion;
                    this.ktsTimingTemplateRowMap.get(this.type).get(event.data.fullVersion).forEach(row => {
                        // do not change current row, but any others using this milestone
                        if (row.id !== event.data.id) {
                            row.milestone = row.milestone.split(/\s*\/\s*/)
                                .map(milestone => milestone === event.oldValue ? event.newValue : milestone).join('/');
                        }
                    });
                    this.refreshTemplateEditorGrid();
                }
            });
        }
    }

    callSave() {
        this.normalizeKtsTemplateRowNumbersBeforeSave();

        const data: ValidationModel[] = Array.from(this.ktsTimingTemplateRowMap.values())
            .map(versionMap =>
                Array.from(versionMap.values()).reduce((a, b) => a.concat(b), [])
            )
            .reduce((acc, rows) => acc.concat(rows), [])
            .filter(row => row.state !== 'saved')
            .map(row => {
                // YesNo enums do not allow binding to blank
                // dropdown options are simple strings, so we need to convert empty strings to null
                if (row.includeInEsorReport === '') {
                    row.includeInEsorReport = null;
                }
                return row;
            });

        this.esciService.saveRows(this, data, 'Timing Template',
            () => this.esciService.refreshKtsTimingTemplateVersionsAfterSave(this, data, true, () => this.setButtonOptionsAndDropdowns()));
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        const api = this.gridApiMap?.get(this.version);
        if (api && !api.isDestroyed()) {
            api.sizeColumnsToFit();
        }
    }

    private deleteDraftTemplate(version: string) {
        this.confirmationService.confirm({
            message: `Are you sure you want to delete this template draft permanently?`,
            header: `Delete Draft Template Version ${version}`,
            key: 'templatePublishConfirmation',
            icon: 'pi pi-exclamation-circle',
            acceptButtonStyleClass: 'p-button-text',
            rejectButtonStyleClass: 'p-button-text',
            acceptIcon: 'pi pi-trash mr-2',
            rejectIcon: 'pi pi-times mr-2',

            accept: () => {
                this.loading = true;
                this.esciService.deleteKtsTemplate(version, this, () => {
                    // remove it from the grid map
                    this.fullCols.get(this.type).delete(version);
                    this.gridApiMap.delete(version);
                    this.esciService.getKtsTimingTemplates(this, true, () => this.setButtonOptionsAndDropdowns());
                });
            }
        });
    }
}
